import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Button } from './ui/button';
import { ArrowLeft, RefreshCw, ExternalLink } from 'lucide-react';

const GameWebView = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [gameUrl, setGameUrl] = useState('');
  const [gameTitle, setGameTitle] = useState('Game');
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    // Get game URL and title from location state or URL params
    const urlParams = new URLSearchParams(location.search);
    const url = urlParams.get('url') || location.state?.gameUrl;
    const title = urlParams.get('title') || location.state?.gameTitle || 'Game';

    if (url) {
      setGameUrl(url);
      setGameTitle(title);
      setIsLoading(false);
    } else {
      setError('No game URL provided');
      setIsLoading(false);
    }
  }, [location]);

  const handleBack = () => {
    navigate(-1);
  };

  const handleRefresh = () => {
    const iframe = document.getElementById('game-iframe');
    if (iframe) {
      iframe.src = iframe.src;
    }
  };

  const handleOpenExternal = () => {
    window.open(gameUrl, '_blank');
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <RefreshCw className="w-8 h-8 animate-spin mx-auto mb-4 text-blue-600" />
          <p className="text-gray-600">Loading game...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="text-red-500 text-xl mb-4">⚠️</div>
          <p className="text-gray-600 mb-4">{error}</p>
          <Button onClick={handleBack} variant="outline">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Go Back
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header - Hidden */}
      <div className="hidden">
        <div className="bg-white border-b border-gray-200 px-4 py-2">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Button
                onClick={handleBack}
                variant="ghost"
                size="sm"
                className="p-2"
              >
                <ArrowLeft className="w-5 h-5" />
              </Button>
              <h1 className="text-lg font-semibold text-gray-900 truncate">
                {gameTitle}
              </h1>
            </div>
            
            <div className="flex items-center gap-2">
              <Button
                onClick={handleRefresh}
                variant="outline"
                size="sm"
                className="p-2"
              >
                <RefreshCw className="w-4 h-4" />
              </Button>
              <Button
                onClick={handleOpenExternal}
                variant="outline"
                size="sm"
                className="p-2"
              >
                <ExternalLink className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Game Container - Full Height */}
      <div className="h-screen">
        <iframe
          id="game-iframe"
          src={gameUrl}
          className="w-full h-full border-0"
          title={gameTitle}
          allow="fullscreen; autoplay; encrypted-media; gyroscope; picture-in-picture"
          sandbox="allow-same-origin allow-scripts allow-forms allow-popups allow-presentation"
          onLoad={() => setIsLoading(false)}
          onError={() => {
            setError('Failed to load game');
            setIsLoading(false);
          }}
        />
      </div>

      {/* Loading Overlay */}
      {isLoading && (
        <div className="fixed inset-0 bg-white bg-opacity-90 flex items-center justify-center z-20">
          <div className="text-center">
            <RefreshCw className="w-8 h-8 animate-spin mx-auto mb-4 text-blue-600" />
            <p className="text-gray-600">Loading {gameTitle}...</p>
          </div>
        </div>
      )}
    </div>
  );
};

export default GameWebView;
